









































































import Vue, { PropType } from 'vue';

// Data
import countries from 'highered-utils/data/countries.json';

// Types and services
import CandidateService, { Candidate } from '~/services/CandidateService';
import CompanyService from '~/services/CompanyService';
import { CandidateStudyProgram } from '~/services/CSPService';
import { commonProps } from '~/utils/candidate';
import { CandidateProfileScore } from '~/utils/candidate/CandidateProfileScorer';
import { ArrayItem } from '~/utils/types';

// Components
import Avatar from '~/components/builders/Avatar.vue';
import AttributeIcon, { AttributeIconType } from '../components/AttributeIcon.vue';
import PersonalDetailsModal from '../modals/PersonalDetailsModal.vue';
import Card from './Card.vue';
import { DateComparerBuilder } from '~/utils/helpers/comparison';

const countryMap = countries.reduce(
    (map, country) => map.set(country.value, country),
    new Map<string, ArrayItem<typeof countries>>()
);

export default Vue.extend({
    name: 'PersonalDetailsConfirm',
    components: {
        Card,
        Avatar,
        PersonalDetailsModal,
        AttributeIcon,
    },
    props: {
        ...commonProps(),
        score: {
            type: Object as PropType<CandidateProfileScore | null>,
            required: false,
            default: null,
        },
        emptyRequiredFields: {
            type: Boolean,
            default: () => false,
            required: false,
        },
    },
    data() {
        return {
            service: new CandidateService(),
            showModal: false,

            schoolLogo: '',
        };
    },
    computed: {
        fullName(): string {
            return [this.user.firstName, this.user.lastName].filter(x => !!x).join(' ') || 'You';
        },
        nationality(): string | null {
            if (this.candidate.nationalities.length === 0) return null;
            const country = countryMap.get(this.candidate.nationalities[0].nationality || '');
            if (!country) return null;
            return country.label;
        },
        location(): string | null {
            const country = countryMap.get(this.candidate.country || '');
            if (!country) return null;
            return country.label;
        },
        pronoun(): string | null {
            if (!this.candidate.pronoun) return null;
            if (this.$te(`profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`))
                return this.$t(
                    `profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`
                ).toString();
            return this.candidate.pronoun.value || null;
        },
        workAuthorizationsStr(): string | null {
            if (Object.keys(this.workAuthorizations).length > 0)
                return this.workAuthorizations.map(x => x.code).join(', ');
            return null;
        },
        badges(): AttributeIconType[] {
            const icons: Record<AttributeIconType, boolean> = {
                job: !!this.candidate.preferences.workPreferences.length,
                flex: !['onsite', null].includes(this.candidate.preferences.eligibleWorkLocation),
                relo: !!this.candidate.preferences.eligibleForRelocation,
                lgb: !!this.candidate.preferences.memberOfLGBTQCommunity,
                eth: this.candidate.ethnicMinority === 'yes',
                pwd: this.candidate.disability === 'yes',
            };
            return Object.keys(icons).filter(
                key => icons[key as keyof typeof icons]
            ) as AttributeIconType[];
        },
        calculateTotalYearsOfWorkExperience(): number {
            const experiences = this.candidate.workExperiences || [];
            let totalYears = 0;
            const now = new Date();

            for (let i = 0; i < experiences.length; i++) {
                const curWE = experiences[i];
                const startDate = curWE.start ? curWE.start : now;
                const endDate = curWE.end ? curWE.end : now;
                if (endDate.getTime() > now.getTime()) endDate.setTime(now.getTime());

                let currentDuration = endDate.getTime() - startDate.getTime();

                // Check for overlapping work experiences
                for (let j = i + 1; j < experiences.length; j++) {
                    const otherExperience = experiences[j];
                    const otherStartDate = otherExperience.start ? otherExperience.start : now;
                    const otherEndDate = otherExperience.end ? otherExperience.end : now;
                    if (otherEndDate.getTime() > now.getTime()) otherEndDate.setTime(now.getTime());

                    const overlapStart = Math.max(startDate.getTime(), otherStartDate.getTime());
                    const overlapEnd = Math.min(endDate.getTime(), otherEndDate.getTime());
                    const overlapDuration = Math.max(0, overlapEnd - overlapStart);

                    // If there is an overlap, subtract the overlap duration from the current duration
                    if (overlapDuration > 0) {
                        currentDuration -= overlapDuration;
                    }
                }
                totalYears += currentDuration / (1000 * 60 * 60 * 24 * 365);
            }
            return Math.round(totalYears);
        },
    },
    methods: {
        getLatestStudyProgram(): CandidateStudyProgram | null {
            const { studyPrograms } = this.candidate;
            if (!studyPrograms || !studyPrograms.length) return null;

            const cmp = new DateComparerBuilder<CandidateStudyProgram>().buildComparer(
                sp => sp.end,
                false,
                true
            ).compare;
            const programs = Object.assign([], studyPrograms).sort(cmp);
            return programs[0];
        },
        async loadSchoolLogo() {
            const program = this.getLatestStudyProgram();
            if (!program || !program.studyProgram.schoolId) return;

            const company = await CompanyService.getCompany(program.studyProgram.schoolId);
            if (!company.data.logo) return;

            this.schoolLogo = company.data.logo;
        },
        handleGenerateCV() {
            console.warn('Not implemented yet');
        },
        handleOpenPersonalDetailsModalEvent() {
            this.showModal = true;
        },
    },
    mounted() {
        this.loadSchoolLogo();

        window.addEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
    beforeDestroy() {
        window.removeEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
});
